import { useState } from 'preact/hooks';
import { route } from 'preact-router';
import { useAuth } from '../../hooks/useAuth';
import { MainLayout } from '../../components/MainLayout';
import { getAuthHeaders } from '../../utils/auth';

type OnboardingStep = 'create-team' | 'add-ai-key' | 'done';

export function OnboardingPage() {
  useAuth();
  const [currentStep, setCurrentStep] = useState<OnboardingStep>('create-team');
  const [teamName, setTeamName] = useState('');
  const [createdTeamId, setCreatedTeamId] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  const [aiProvider, setAiProvider] = useState<'openai' | 'google'>('openai');
  const [aiKey, setAiKey] = useState('');
  const [isSavingKey, setIsSavingKey] = useState(false);
  const [keyError, setKeyError] = useState<string | null>(null);
  const [keySkipped, setKeySkipped] = useState(false);

  const handleCreateTeam = async () => {
    if (!teamName.trim()) {
      setCreateError('Team name is required');
      return;
    }

    setIsCreating(true);
    setCreateError(null);

    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/teams`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(await getAuthHeaders()),
        },
        body: JSON.stringify({ name: teamName.trim() }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to create team');
      }

      const result = await response.json();
      setCreatedTeamId(result.team.teamId);
      setCurrentStep('add-ai-key');
    } catch (err: any) {
      setCreateError(err.message || 'Failed to create team. Please try again.');
    } finally {
      setIsCreating(false);
    }
  };

  const handleSaveAiKey = async () => {
    if (!aiKey.trim() || !createdTeamId) return;

    setIsSavingKey(true);
    setKeyError(null);

    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/teams/${createdTeamId}/ai-key`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(await getAuthHeaders()),
        },
        body: JSON.stringify({ provider: aiProvider, apiKey: aiKey.trim() }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to save API key');
      }

      setCurrentStep('done');
    } catch (err: any) {
      setKeyError(err.message || 'Failed to save API key. Check that the key is valid.');
    } finally {
      setIsSavingKey(false);
    }
  };

  const handleSkipKey = () => {
    setKeySkipped(true);
    setCurrentStep('done');
  };

  const handleFinish = async () => {
    if (!createdTeamId) return;

    try {
      await fetch(`${import.meta.env.VITE_API_URL}/teams/${createdTeamId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          ...(await getAuthHeaders()),
        },
        body: JSON.stringify({ hasCompletedOnboarding: true }),
      });
    } catch {
      // non-blocking
    }

    route(`/teams/${createdTeamId}`);
  };

  const steps: OnboardingStep[] = ['create-team', 'add-ai-key', 'done'];
  const currentIndex = steps.indexOf(currentStep);

  const progressDots = (
    <div class="flex gap-2 justify-center mt-8">
      {steps.map((_, i) => (
        <div key={i} class={`w-2 h-2 rounded-full ${i <= currentIndex ? 'bg-primary' : 'bg-base-300'}`} />
      ))}
    </div>
  );

  return (
    <MainLayout>
      <div class="flex-1 flex items-center justify-center p-8">
        <div class="card w-full max-w-lg bg-base-100 shadow-xl">
          <div class="card-body p-8">

            {/* Step 1: Create Team */}
            {currentStep === 'create-team' && (
              <>
                <h2 class="text-3xl font-bold mb-2">Create Your Team</h2>
                <p class="text-base-content/70 mb-8">You'll be the admin — invite others later.</p>

                {createError && (
                  <div class="alert alert-error mb-4">
                    <span>{createError}</span>
                  </div>
                )}

                <div class="form-control mb-8">
                  <label class="label">
                    <span class="label-text font-medium">Team Name</span>
                  </label>
                  <input
                    type="text"
                    placeholder="Acme Corp"
                    class="input input-bordered input-lg w-full"
                    value={teamName}
                    onInput={(e) => setTeamName((e.target as HTMLInputElement).value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleCreateTeam()}
                    autoFocus
                  />
                </div>

                <button
                  class="btn btn-primary btn-lg w-full"
                  onClick={handleCreateTeam}
                  disabled={isCreating}
                >
                  {isCreating ? (
                    <><span class="loading loading-spinner loading-sm"></span>Creating...</>
                  ) : (
                    'Create Team →'
                  )}
                </button>

                {progressDots}
              </>
            )}

            {/* Step 2: Add AI Key */}
            {currentStep === 'add-ai-key' && (
              <>
                <h2 class="text-3xl font-bold mb-2">Add Your AI Key</h2>
                <p class="text-base-content/70 mb-6">
                  Zenmako uses your own API key — you pay your AI provider directly, never us.
                </p>

                {keyError && (
                  <div class="alert alert-error mb-4">
                    <span>{keyError}</span>
                  </div>
                )}

                <div class="form-control mb-4">
                  <label class="label">
                    <span class="label-text font-medium">AI Provider</span>
                  </label>
                  <select
                    class="select select-bordered w-full"
                    value={aiProvider}
                    onChange={(e) => setAiProvider((e.target as HTMLSelectElement).value as 'openai' | 'google')}
                  >
                    <option value="openai">OpenAI (GPT-4o, o3, o4)</option>
                    <option value="google">Google AI (Gemini)</option>
                  </select>
                </div>

                <div class="form-control mb-6">
                  <label class="label">
                    <span class="label-text font-medium">API Key</span>
                  </label>
                  <input
                    type="password"
                    placeholder={aiProvider === 'openai' ? 'sk-...' : 'AIza...'}
                    class="input input-bordered w-full font-mono"
                    value={aiKey}
                    onInput={(e) => setAiKey((e.target as HTMLInputElement).value)}
                  />
                  <label class="label">
                    <span class="label-text-alt text-base-content/50">
                      Stored encrypted in AWS — never logged or returned to the browser.
                    </span>
                  </label>
                </div>

                <button
                  class="btn btn-primary w-full mb-3"
                  onClick={handleSaveAiKey}
                  disabled={isSavingKey || !aiKey.trim()}
                >
                  {isSavingKey ? (
                    <><span class="loading loading-spinner loading-sm"></span>Validating & saving...</>
                  ) : (
                    'Save API Key →'
                  )}
                </button>

                <button class="btn btn-ghost w-full" onClick={handleSkipKey}>
                  Skip for now
                </button>

                {progressDots}
              </>
            )}

            {/* Step 3: Done */}
            {currentStep === 'done' && (
              <>
                <div class="text-center mb-8">
                  <div class="text-6xl mb-4">{keySkipped ? '🚀' : '🎉'}</div>
                  <h2 class="text-3xl font-bold mb-3">
                    {keySkipped ? "You're Almost Ready" : "You're All Set!"}
                  </h2>
                  <p class="text-base-content/70">
                    {keySkipped
                      ? `Team "${teamName}" is created. Add an AI key in Settings to run workflows.`
                      : `Team "${teamName}" is ready with your AI key configured.`}
                  </p>
                </div>

                {keySkipped && (
                  <div class="alert alert-warning mb-6">
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span>You'll need an AI key before workflows can run. Add one in <strong>Team Settings → AI Provider</strong>.</span>
                  </div>
                )}

                <button
                  class="btn btn-primary btn-lg w-full"
                  onClick={handleFinish}
                >
                  Go to Dashboard →
                </button>

                {progressDots}
              </>
            )}

          </div>
        </div>
      </div>
    </MainLayout>
  );
}
